/**
 * Form Submission Service
 * Handles all form submission-related API calls
 * Updated for API v3.1 with typed submission data
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { FORM_SUBMISSION_ENDPOINTS, FORM_SUBMISSION_QUERY_ENDPOINTS, buildQueryString } from '../endpoints';
import { SubmissionStatus, getSubmissionStatusLabel, getSubmissionStatusColor } from '../../types/enums';

/**
 * @typedef {import('../../types/formSchema').FormSubmissionData} FormSubmissionData
 * @typedef {import('../../types/formSchema').SubmissionMetadata} SubmissionMetadata
 */

/**
 * @typedef {Object} CreateFormSubmissionDto
 * @property {number} formSchemaId - Form schema ID (required)
 * @property {FormSubmissionData} submissionData - Typed submission data (required, API v3.1+)
 * @property {string} [submitterName] - Submitter name (max 200 chars)
 * @property {string} [submitterEmail] - Submitter email (max 320 chars)
 * @property {number} [latitude] - GPS latitude
 * @property {number} [longitude] - GPS longitude
 * @property {string} [locationName] - Location name (max 500 chars)
 * @property {string} [deviceId] - Device identifier (max 100 chars)
 * @property {boolean} [isOfflineSubmission] - Whether submitted offline
 * @property {string} [deviceTimestamp] - Device timestamp (ISO 8601)
 * @property {number} [workOrderId] - Optional work order ID to link this submission to
 */

/**
 * @typedef {Object} UpdateSubmissionStatusDto
 * @property {string} status - Submission status (required) - must be one of: Draft, InProgress, Submitted, UnderReview, Approved, Rejected, Archived
 * @property {string} [comments] - Status change comments (max 1000 chars)
 */

/**
 * @typedef {Object} SubmissionListParams
 * @property {string} [status] - Filter by status (Draft, InProgress, Submitted, UnderReview, Approved, Rejected, Archived)
 * @property {number} [formSchemaId] - Filter by form schema ID
 * @property {string} [fromDate] - Filter from date (ISO 8601)
 * @property {string} [toDate] - Filter to date (ISO 8601)
 * @property {number} [page] - Page number (default: 1)
 * @property {number} [pageSize] - Page size (default: 20)
 * @property {boolean} [includeChildren] - When true, also returns submissions
 *   that already have a parent (used by the "Link existing" picker so child
 *   submissions of the target schema remain selectable). Default false keeps
 *   the dashboard list parent-only.
 */

// Re-export from enums for backwards compatibility
export { SubmissionStatus, getSubmissionStatusLabel, getSubmissionStatusColor };

const formSubmissionService = {
  /**
   * Get list of form submissions
   * @param {SubmissionListParams} [params] - Query parameters
   * @returns {Promise<Object>} Paginated list of submissions
   */
  getSubmissions: async (params = {}) => {
    const queryString = buildQueryString(params);
    return apiRequest(() =>
      axiosInstance.get(`${FORM_SUBMISSION_ENDPOINTS.LIST}${queryString}`)
    );
  },

  /**
   * Get submission by ID with optional children
   * @param {number} id - Submission ID
   * @param {Object} [options] - Options
   * @param {boolean} [options.includeChildren] - Include child submissions
   * @returns {Promise<Object>} Submission data
   */
  getSubmissionById: async (id, options = {}) => {
    const queryString = buildQueryString(options);
    return apiRequest(() =>
      axiosInstance.get(`${FORM_SUBMISSION_ENDPOINTS.GET_BY_ID(id)}${queryString}`)
    );
  },

  /**
   * Get child submissions for a parent submission
   * @param {number} parentId - Parent submission ID
   * @returns {Promise<Array>} Array of child submissions
   */
  getChildSubmissions: async (parentId) => {
    return apiRequest(() =>
      axiosInstance.get(FORM_SUBMISSION_ENDPOINTS.CHILDREN(parentId))
    );
  },

  /**
   * Link an existing submission to a parent as a repeated-form child.
   * @param {number} parentId
   * @param {{ childSubmissionId: number, fieldId: string, instanceIndex?: number }} body
   * @returns {Promise<Object>} Link response DTO
   */
  linkExistingChild: async (parentId, body) => {
    return apiRequest(() =>
      axiosInstance.post(FORM_SUBMISSION_ENDPOINTS.LINK_CHILD(parentId), body)
    );
  },

  /**
   * Remove a link between a parent and a child under a specific RepeatedForm field.
   * The child submission itself is preserved.
   * @param {number} parentId
   * @param {number} childId
   * @param {string} fieldId
   * @returns {Promise<void>}
   */
  unlinkChild: async (parentId, childId, fieldId) => {
    return apiRequest(() =>
      axiosInstance.delete(FORM_SUBMISSION_ENDPOINTS.UNLINK_CHILD(parentId, childId), {
        params: { fieldId }
      })
    );
  },

  /**
   * Get the impact of deleting a submission: counts of children, orphans,
   * and children shared with other parents. Used to drive the delete
   * confirmation modal.
   * @param {number} id
   * @returns {Promise<{ childCount: number, orphanCount: number, sharedCount: number }>}
   */
  getDeleteImpact: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(FORM_SUBMISSION_ENDPOINTS.DELETE_IMPACT(id))
    );
  },

  /**
   * Create new form submission
   * @param {CreateFormSubmissionDto} submissionData - Submission data
   * @returns {Promise<Object>} Created submission
   */
  createSubmission: async (submissionData) => {
    return apiRequest(() =>
      axiosInstance.post(FORM_SUBMISSION_ENDPOINTS.CREATE, submissionData)
    );
  },

  /**
   * Update the submitted field data for an existing submission.
   * SuperAdmin and OrgAdmin can edit any; regular users can only edit their own.
   * Optional metadata overrides (location / datetime / submitter) are applied server-side only
   * when the organization has the matching flag enabled (submitter is admin-only).
   * @param {number} id - Submission ID
   * @param {FormSubmissionData} submissionData - Updated submission data ({ fields: {...} })
   * @param {Object} [meta] - Optional metadata: { latitude, longitude, locationName, submittedAt, submitterName, submitterEmail }
   * @returns {Promise<Object>} Updated submission response DTO
   */
  updateSubmission: async (id, submissionData, meta = {}) => {
    return apiRequest(() =>
      axiosInstance.put(FORM_SUBMISSION_ENDPOINTS.UPDATE_DATA(id), { submissionData, ...meta })
    );
  },

  /**
   * Assign or reassign a work order to a submission. Pass null to unlink.
   * @param {number} id - Submission ID
   * @param {number|null} workOrderId - Work order ID or null to unlink
   * @returns {Promise<Object>} Updated submission
   */
  assignWorkOrder: async (id, workOrderId) => {
    return apiRequest(() =>
      axiosInstance.put(FORM_SUBMISSION_ENDPOINTS.ASSIGN_WORK_ORDER(id), { workOrderId })
    );
  },

  /**
   * Update submission status
   * @param {number} id - Submission ID
   * @param {UpdateSubmissionStatusDto} statusData - Status update data
   * @returns {Promise<Object>} Updated submission
   */
  updateSubmissionStatus: async (id, statusData) => {
    return apiRequest(() =>
      axiosInstance.put(FORM_SUBMISSION_ENDPOINTS.UPDATE_STATUS(id), statusData)
    );
  },

  /**
   * Approve submission
   * @param {number} id - Submission ID
   * @param {string} [comments] - Approval comments
   * @returns {Promise<Object>} Approved submission
   */
  approveSubmission: async (id, comments = '') => {
    return apiRequest(() =>
      axiosInstance.post(FORM_SUBMISSION_ENDPOINTS.APPROVE(id), JSON.stringify(comments), {
        headers: {
          'Content-Type': 'application/json',
        },
      })
    );
  },

  /**
   * Reject submission
   * @param {number} id - Submission ID
   * @param {string} [comments] - Rejection reason
   * @returns {Promise<Object>} Rejected submission
   */
  rejectSubmission: async (id, comments = '') => {
    return apiRequest(() =>
      axiosInstance.post(FORM_SUBMISSION_ENDPOINTS.REJECT(id), JSON.stringify(comments), {
        headers: {
          'Content-Type': 'application/json',
        },
      })
    );
  },

  /**
   * Delete submission (soft delete - sets isActive = false).
   * When deleteOrphans is true, any children that would be orphaned by
   * this deletion are soft-deleted as well; shared children are preserved.
   * @param {number} id - Submission ID
   * @param {{ deleteOrphans?: boolean }} [options]
   * @returns {Promise<void>}
   */
  deleteSubmission: async (id, options = {}) => {
    const { deleteOrphans = false } = options;
    return apiRequest(() =>
      axiosInstance.delete(FORM_SUBMISSION_ENDPOINTS.DELETE(id), {
        params: deleteOrphans ? { deleteOrphans: true } : undefined
      })
    );
  },

  /**
   * Delete multiple submissions at once (soft delete)
   * @param {Array<number>} ids - Array of submission IDs to delete
   * @returns {Promise<Object>} Result with deletedCount
   */
  bulkDeleteSubmissions: async (ids) => {
    // Validation
    if (!ids || !Array.isArray(ids) || ids.length === 0) {
      throw new Error('Submission IDs array is required and cannot be empty');
    }

    if (ids.length > 100) {
      throw new Error('Cannot delete more than 100 submissions at once');
    }

    return apiRequest(() =>
      axiosInstance.delete(FORM_SUBMISSION_ENDPOINTS.BULK_DELETE, {
        data: { ids }
      })
    );
  },

  /**
   * Reactivate (restore) soft-deleted submission
   * @param {number} id - Submission ID to restore
   * @returns {Promise<Object>} Restored submission object
   */
  activateSubmission: async (id) => {
    return apiRequest(() =>
      axiosInstance.post(FORM_SUBMISSION_ENDPOINTS.ACTIVATE(id))
    );
  },

  /**
   * Helper: Get submissions by form schema ID
   * @param {number} formSchemaId - Form schema ID
   * @param {Object} [additionalParams] - Additional query parameters
   * @returns {Promise<Object>} Filtered submissions
   */
  getSubmissionsByFormId: async (formSchemaId, additionalParams = {}) => {
    return formSubmissionService.getSubmissions({
      formSchemaId,
      ...additionalParams,
    });
  },

  /**
   * Helper: Get submissions by status
   * @param {string} status - Submission status
   * @param {Object} [additionalParams] - Additional query parameters
   * @returns {Promise<Object>} Filtered submissions
   */
  getSubmissionsByStatus: async (status, additionalParams = {}) => {
    return formSubmissionService.getSubmissions({
      status,
      ...additionalParams,
    });
  },

  // ===== NEW: Form Submission Query Endpoints =====

  /**
   * Fetch fields for a specific submission from the API
   * @param {number} submissionId - Submission ID
   * @returns {Promise<Object>} Submission fields
   */
  fetchSubmissionFields: async (submissionId) => {
    return apiRequest(() =>
      axiosInstance.get(`/api/FormSubmissionQuery/fields/${submissionId}`)
    );
  },

  /**
   * Export submissions to CSV
   * @param {Object} [exportParams] - Export parameters
   * @param {number} [exportParams.formSchemaId] - Filter by form schema ID
   * @param {string} [exportParams.fromDate] - Filter from date (ISO 8601)
   * @param {string} [exportParams.toDate] - Filter to date (ISO 8601)
   * @returns {Promise<Blob>} CSV file data
   */
  exportToCSV: async (exportParams = {}) => {
    const queryString = buildQueryString(exportParams);
    return apiRequest(() =>
      axiosInstance.get(`/api/FormSubmissionQuery/export/csv${queryString}`, {
        responseType: 'blob',
      })
    );
  },

  /**
   * Get autocomplete suggestions for a form field based on prior submission values
   * @param {number} formSchemaId - Form schema ID
   * @param {string} fieldKey - Field key
   * @param {string} prefix - Prefix to match (min 2 chars)
   * @param {number} [limit] - Max suggestions (1–20, default 10)
   * @returns {Promise<string[]>} Matching string values ordered by frequency
   */
  getFieldSuggestions: async (formSchemaId, fieldKey, prefix, limit = 10) => {
    const queryString = buildQueryString({ formSchemaId, fieldKey, prefix, limit });
    return apiRequest(() =>
      axiosInstance.get(`${FORM_SUBMISSION_QUERY_ENDPOINTS.SUGGESTIONS}${queryString}`)
    );
  },

  /**
   * Export submissions to Excel (.xlsx) with configurable options
   * @param {Object} config - Excel export configuration
   * @param {number} config.formSchemaId - Form schema ID
   * @param {string} [config.fromDate] - Start date filter (ISO 8601)
   * @param {string} [config.toDate] - End date filter (ISO 8601)
   * @param {number[]} [config.submissionIds] - Specific submission IDs
   * @param {boolean} [config.includeInactive] - Include deleted submissions
   * @param {string[]} config.selectedSystemFields - System fields to include
   * @param {string[]} config.selectedFields - Form field keys to include
   * @param {Object} [config.dateFormatOptions] - Date/time format settings
   * @param {Object} [config.repeatedFormHandling] - Per-field repeated form handling
   * @param {string} [config.timezoneId] - IANA timezone ID
   * @returns {Promise<Blob>} Excel file data
   */
  exportToExcel: async (config) => {
    return apiRequest(() =>
      axiosInstance.post(FORM_SUBMISSION_QUERY_ENDPOINTS.EXPORT_EXCEL, config, {
        responseType: 'blob',
      })
    );
  },

  // ===== Comments =====

  /**
   * Get comments for a submission
   * @param {number} submissionId - Submission ID
   * @returns {Promise<Array>} List of comments
   */
  getComments: async (submissionId) => {
    return apiRequest(() =>
      axiosInstance.get(FORM_SUBMISSION_ENDPOINTS.COMMENTS(submissionId))
    );
  },

  /**
   * Add a comment to a submission
   * @param {number} submissionId - Submission ID
   * @param {string} content - Comment text
   * @returns {Promise<Object>} Created comment
   */
  addComment: async (submissionId, content) => {
    return apiRequest(() =>
      axiosInstance.post(FORM_SUBMISSION_ENDPOINTS.COMMENTS(submissionId), { content })
    );
  },

  /**
   * Delete a comment (admin only)
   * @param {number} commentId - Comment ID
   * @returns {Promise<void>}
   */
  deleteComment: async (commentId) => {
    return apiRequest(() =>
      axiosInstance.delete(FORM_SUBMISSION_ENDPOINTS.DELETE_COMMENT(commentId))
    );
  },

  /**
   * Get unread comment counts for submissions in the current user's org
   * @returns {Promise<Object>} { [submissionId]: unreadCount }
   */
  getUnreadCommentCounts: async () => {
    return apiRequest(() =>
      axiosInstance.get(FORM_SUBMISSION_ENDPOINTS.UNREAD_COMMENT_COUNTS)
    );
  },

  // ===== Helper Methods for API v3.1 =====

  /**
   * Helper: Create typed submission data object (API v3.1+)
   * @param {Object} fields - Field values as key-value pairs
   * @param {Object} [options] - Additional options
   * @param {string} [options.clientVersion] - Client application version
   * @param {string} [options.userAgent] - Browser user agent
   * @param {number} [options.formDurationSeconds] - Time spent filling the form
   * @param {Object.<string, string>} [options.customData] - Custom metadata
   * @returns {FormSubmissionData} Typed submission data object
   */
  createSubmissionDataObject: (fields, options = {}) => {
    const submissionData = {
      fields: fields || {},
    };

    // Add metadata if any options provided
    const { clientVersion, userAgent, formDurationSeconds, customData } = options;
    if (clientVersion || userAgent || formDurationSeconds !== undefined || customData) {
      submissionData.metadata = {};
      if (clientVersion) submissionData.metadata.clientVersion = clientVersion;
      if (userAgent) submissionData.metadata.userAgent = userAgent;
      if (formDurationSeconds !== undefined) submissionData.metadata.formDurationSeconds = formDurationSeconds;
      if (customData) submissionData.metadata.customData = customData;
    }

    return submissionData;
  },

  /**
   * Helper: Extract field values from submission data (API v3.1+)
   * @param {FormSubmissionData|string} submissionData - Submission data (typed object or legacy JSON string)
   * @returns {Object} Field values object
   */
  getSubmissionFields: (submissionData) => {
    // Handle new typed format
    if (submissionData && typeof submissionData === 'object' && submissionData.fields) {
      return submissionData.fields;
    }

    // Handle legacy JSON string format (backward compatibility)
    if (typeof submissionData === 'string') {
      try {
        const parsed = JSON.parse(submissionData);
        // If parsed result has fields property, return it
        if (parsed && typeof parsed === 'object' && parsed.fields) {
          return parsed.fields;
        }
        // Otherwise, return the whole parsed object (very old format)
        return parsed;
      } catch (error) {
        console.error('Error parsing submission data:', error);
        return {};
      }
    }

    return {};
  },

  /**
   * Helper: Validate submission data before creation (API v3.1+)
   * @param {CreateFormSubmissionDto} submissionData - Submission data to validate
   * @returns {Object} Validation result { isValid: boolean, errors: string[] }
   */
  validateSubmissionData: (submissionData) => {
    const errors = [];

    if (!submissionData.formSchemaId) {
      errors.push('Form schema ID is required');
    }

    if (!submissionData.submissionData) {
      errors.push('Submission data is required');
    } else if (typeof submissionData.submissionData === 'object') {
      // Validate new typed format
      if (!submissionData.submissionData.fields || typeof submissionData.submissionData.fields !== 'object') {
        errors.push('Submission data must have a fields object');
      }
    } else {
      errors.push('Submission data must be a FormSubmissionData object');
    }

    if (submissionData.submitterName && submissionData.submitterName.length > 200) {
      errors.push('Submitter name must not exceed 200 characters');
    }

    if (submissionData.submitterEmail && submissionData.submitterEmail.length > 320) {
      errors.push('Submitter email must not exceed 320 characters');
    }

    if (submissionData.locationName && submissionData.locationName.length > 500) {
      errors.push('Location name must not exceed 500 characters');
    }

    if (submissionData.deviceId && submissionData.deviceId.length > 100) {
      errors.push('Device ID must not exceed 100 characters');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Helper: Format date for API
   * @param {Date|string} date - Date to format
   * @returns {string} ISO 8601 formatted date
   */
  formatDateForApi: (date) => {
    if (date instanceof Date) {
      return date.toISOString();
    }
    return new Date(date).toISOString();
  },

  // ===== DEPRECATED: Legacy Helper Methods (for backward compatibility) =====

  /**
   * @deprecated Use getSubmissionFields() instead. This method is kept for backward compatibility.
   * Helper: Parse submission data JSON string to object
   * @param {string} submissionData - JSON string
   * @returns {Object|null} Parsed submission object
   */
  parseSubmissionData: (submissionData) => {
    console.warn('parseSubmissionData() is deprecated. Use getSubmissionFields() instead.');
    try {
      return JSON.parse(submissionData);
    } catch (error) {
      console.error('Error parsing submission data:', error);
      return null;
    }
  },

  /**
   * @deprecated No longer needed with typed submission data. This method is kept for backward compatibility.
   * Helper: Stringify submission object to JSON
   * @param {Object} submissionObject - Submission object
   * @returns {string|null} JSON string
   */
  stringifySubmissionData: (submissionObject) => {
    console.warn('stringifySubmissionData() is deprecated. Use createSubmissionDataObject() instead.');
    try {
      return JSON.stringify(submissionObject);
    } catch (error) {
      console.error('Error stringifying submission data:', error);
      return null;
    }
  },
};

export default formSubmissionService;
